import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { appeler } from "./client";

/**
 * Enveloppe standard du back : une réponse porte TOUJOURS ses avis,
 * y compris quand elle refuse (§11.19 — un refus doit porter ses avertissements).
 */
export const AvisSchema = z.object({
  niveau: z.union([z.enum(["Information", "Attention", "Blocage"]), z.number()]),
  code: z.string(),
  message: z.string(),
  detail: z.string().nullable().optional(),
});
export type Avis = z.infer<typeof AvisSchema>;

/** Normalise le niveau : l'API peut l'émettre en enum numérique ou en texte. */
export function niveauDe(a: Avis): "Information" | "Attention" | "Blocage" {
  if (typeof a.niveau === "number") {
    return (["Information", "Attention", "Blocage"] as const)[a.niveau] ?? "Information";
  }
  return a.niveau;
}

export const enveloppe = <T extends z.ZodTypeAny>(valeur: T) =>
  z.object({
    valeur: valeur.nullable(),
    avis: z.array(AvisSchema).default([]),
    abouti: z.boolean().optional(),
  });

/** Requête GET typée, avec le contrat vérifié à l'appel. */
export function useApi<T>(
  cle: readonly unknown[],
  chemin: string,
  schema: z.ZodType<T>,
  options: { actif?: boolean } = {},
) {
  return useQuery({
    queryKey: cle,
    // TanStack Query fournit un `signal` et l'abandonne quand la requête est
    // remplacée ou que le composant se démonte. L'ignorer laissait le serveur
    // travailler pour rien — et faisait remonter une annulation jusqu'à Kestrel.
    queryFn: ({ signal }) => appeler(chemin, schema, { signal }),
    enabled: options.actif ?? true,
  });
}

/** Requête GET sur une réponse enveloppée (valeur + avis). */
export function useApiEnveloppe<T extends z.ZodTypeAny>(
  cle: readonly unknown[],
  chemin: string,
  schema: T,
  options: { actif?: boolean } = {},
) {
  return useQuery({
    queryKey: cle,
    queryFn: ({ signal }) => appeler(chemin, enveloppe(schema), { signal }),
    enabled: options.actif ?? true,
  });
}

export function useEnvoi<TCorps, TReponse>(
  chemin: string,
  schema: z.ZodType<TReponse>,
  options: { methode?: string; invalider?: readonly unknown[][] } = {},
) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (corps: TCorps) =>
      appeler(chemin, schema, {
        method: options.methode ?? "POST",
        body: JSON.stringify(corps),
      }),
    onSuccess: () => {
      for (const cle of options.invalider ?? []) {
        void qc.invalidateQueries({ queryKey: cle });
      }
    },
  });
}

/** Formate un montant en francs CFA. Aucun symbole exotique. */
export const fcfa = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : `${Math.round(v).toLocaleString("fr-FR")} F`;

export const pourcent = (v: number | null | undefined, decimales = 1) =>
  v === null || v === undefined ? "—" : `${(v * 100).toFixed(decimales)} %`;

export const MOIS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
] as const;
